import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import apiClient from 'utils/apiClient';

interface SessionState {
    expiresAt: number | null; // 절대 만료 시각 (ms timestamp)
    remainingSeconds: number | null;
    maxInactiveInterval: number; // 서버 세션 타임아웃 (초)
    warningSeconds: number; // 경고 표시 기준 (서버에서 환경별로 설정)
    showWarning: boolean;
    showAlert: boolean;
    isExtending: boolean;
    dismissed: boolean;
}

export const initialState: SessionState = {
    expiresAt: null,
    remainingSeconds: null,
    maxInactiveInterval: 1800,
    warningSeconds: 300,
    showWarning: false,
    showAlert: false,
    isExtending: false,
    dismissed: false,
};

export const fetchSessionInfo = createAsyncThunk(
    'session/fetchInfo',
    async () => {
        const response = await apiClient.get<{ remainingSeconds: number; maxInactiveInterval: number; warningSeconds: number }>('/api/session/info');
        return response.data;
    }
);

export const extendSession = createAsyncThunk(
    'session/extend',
    async () => {
        const response = await apiClient.post<{ remainingSeconds: number }>('/api/session/extend');
        return response.data;
    }
);

const sessionSlice = createSlice({
    name: 'session',
    initialState,
    reducers: {
        // 만료/경고 판정은 서버가 WebSocket으로 push한다. tick 은 경고 배너의
        // 카운트다운 숫자를 갱신하는 순수 표시 용도이며 경고를 띄우지 않는다.
        tick(state) {
            if (state.expiresAt === null) return;
            const remaining = Math.floor((state.expiresAt - Date.now()) / 1000);
            state.remainingSeconds = Math.max(0, remaining);
        },
        // 서버가 "곧 만료" 를 push했을 때. 배너(showWarning=Extend 어포던스)는 경고 구간 내내 유지하고,
        // 모달(showAlert)만 사용자가 Close(dismiss)하면 다시 뜨지 않도록 한다.
        sessionWarning(state, action: PayloadAction<number>) {
            const remainingSeconds = action.payload;
            state.remainingSeconds = remainingSeconds;
            state.expiresAt = Date.now() + remainingSeconds * 1000;
            state.showWarning = true;
            if (!state.dismissed) {
                state.showAlert = true;
            }
        },
        // 서버가 "연장됨" 을 push했을 때 (다른 탭/활동으로 세션이 연장된 경우 포함)
        sessionExtended(state, action: PayloadAction<number>) {
            const remainingSeconds = action.payload;
            state.remainingSeconds = remainingSeconds;
            state.expiresAt = Date.now() + remainingSeconds * 1000;
            state.showWarning = false;
            state.showAlert = false;
            state.dismissed = false;
        },
        // "Close" closes the modal only. The banner (with the Extend button) stays so the user can still
        // extend; `dismissed` just stops a later WARNING push from re-opening the modal they closed.
        dismissAlert(state) {
            state.showAlert = false;
            state.dismissed = true;
        },
        resetSession(state) {
            state.expiresAt = null;
            state.remainingSeconds = null;
            state.showWarning = false;
            state.showAlert = false;
            state.isExtending = false;
            state.dismissed = false;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchSessionInfo.fulfilled, (state, action) => {
                const { remainingSeconds, maxInactiveInterval, warningSeconds } = action.payload;
                state.expiresAt = Date.now() + (remainingSeconds * 1000);
                state.remainingSeconds = remainingSeconds;
                state.maxInactiveInterval = maxInactiveInterval;
                state.warningSeconds = warningSeconds;
                if (remainingSeconds <= warningSeconds) {
                    state.showWarning = true;
                    if (!state.dismissed) {
                        state.showAlert = true;
                    }
                } else {
                    state.showWarning = false;
                    state.showAlert = false;
                    state.dismissed = false;
                }
            })
            .addCase(extendSession.pending, (state) => {
                state.isExtending = true;
            })
            .addCase(extendSession.fulfilled, (state, action) => {
                state.expiresAt = Date.now() + (action.payload.remainingSeconds * 1000);
                state.remainingSeconds = action.payload.remainingSeconds;
                state.showWarning = false;
                state.showAlert = false;
                state.isExtending = false;
                state.dismissed = false;
            })
            .addCase(extendSession.rejected, (state) => {
                state.isExtending = false;
            });
    },
});

export const { tick, sessionWarning, sessionExtended, dismissAlert, resetSession } = sessionSlice.actions;
export default sessionSlice.reducer;
